import { useState } from 'react';
import { useMentiSession } from '../../../context/MentiSessionContext';
import {
  startMentiSession,
  endMentiSession,
} from '../../../services/mentiSessionService';
import QRCode from '../../common/QRCode';

export default function MentiSessionControl({ onBack, onPresent }) {
  const { session, loading, error } = useMentiSession();
  const [actionLoading, setActionLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-gray-500 font-medium">Lastar okt...</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-background-light flex flex-col items-center justify-center p-6 text-center">
        <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">error</span>
        <h3 className="text-lg font-bold text-gray-700 mb-2">Fann ikkje okta</h3>
        <p className="text-gray-500 mb-4">{error || 'Ukjend feil'}</p>
        <button
          onClick={onBack}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors"
        >
          Tilbake
        </button>
      </div>
    );
  }

  const participants = session.participants ? Object.values(session.participants) : [];
  const participantCount = participants.length;
  const joinUrl = `${window.location.origin}/menti/join`;

  const handleStart = async () => {
    if (session.status !== 'waiting') return;
    setActionLoading(true);
    try {
      await startMentiSession(session.id);
      onPresent();
    } catch (err) {
      console.error('Error starting menti session:', err);
      alert('Klarte ikkje starte okta. Prov igjen.');
    }
    setActionLoading(false);
  };

  const handleEnd = async () => {
    const confirmed = window.confirm('Er du sikker pa at du vil avslutte okta?');
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await endMentiSession(session.id);
    } catch (err) {
      console.error('Error ending menti session:', err);
      alert('Klarte ikkje avslutte okta. Prov igjen.');
    }
    setActionLoading(false);
  };

  const statusLabel = session.status === 'waiting' ? 'Ventar' : session.status === 'active' ? 'Aktiv' : 'Avslutta';
  const statusColor = session.status === 'active' ? 'bg-green-100 text-green-700' : session.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600';

  return (
    <div className="min-h-screen bg-background-light font-display text-[#0d121b]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-light/95 backdrop-blur-sm p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex size-10 items-center justify-center rounded-full bg-white shadow-ambient hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600">arrow_back</span>
          </button>
          <div>
            <h2 className="text-xl font-bold">Menti-okt</h2>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
          </div>
        </div>
      </header>

      <div className="px-4 space-y-6">
        {/* PIN Display */}
        <div className="bg-white rounded-2xl shadow-ambient p-6 text-center">
          <p className="text-sm font-medium text-gray-500 mb-1">Del PIN med deltakarane</p>
          <div className="text-5xl font-extrabold tracking-[0.3em] text-blue-700 mb-3">
            {session.id}
          </div>
          <p className="text-sm text-gray-400 mb-4">Ga til <span className="font-bold text-blue-600">{joinUrl}</span></p>

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowQR(!showQR)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-sm font-bold hover:bg-blue-100 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">qr_code_2</span>
              {showQR ? 'Goyms QR' : 'Vis QR-kode'}
            </button>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(session.id);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-200 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">content_copy</span>
              Kopier PIN
            </button>
          </div>

          {showQR && (
            <div className="mt-4 flex justify-center">
              <QRCode sessionId={session.id} size={180} joinPath="/menti/join" />
            </div>
          )}
        </div>

        {/* Participant List */}
        <div className="bg-white rounded-2xl shadow-ambient p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-lg">Deltakarar</h3>
            <span className="text-sm font-bold text-blue-600">{participantCount}</span>
          </div>

          {participantCount === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">person_add</span>
              <p className="text-gray-500 text-sm">Ingen deltakarar enno</p>
              <p className="text-gray-400 text-xs mt-1">Del PIN-koden sa folk kan bli med</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {participants.map((p, idx) => (
                <div key={p.id || idx} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center size-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                  <span className="text-sm font-medium">{p.name || 'Ukjend'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pb-8">
          {session.status === 'waiting' && (
            <button
              onClick={handleStart}
              disabled={actionLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl font-bold text-lg hover:brightness-110 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/20"
            >
              <span className="material-symbols-outlined text-[24px]">play_arrow</span>
              {actionLoading ? 'Startar...' : 'Start presentasjon'}
            </button>
          )}

          {session.status === 'active' && (
            <button
              onClick={() => onPresent()}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl font-bold text-lg hover:brightness-110 transition-all shadow-lg shadow-blue-500/20"
            >
              <span className="material-symbols-outlined text-[24px]">slideshow</span>
              Presenter
            </button>
          )}

          {session.status !== 'finished' && (
            <button
              onClick={handleEnd}
              disabled={actionLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-2xl font-bold text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">stop</span>
              Avslutt okt
            </button>
          )}

          {session.status === 'finished' && (
            <div className="text-center py-4">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">check_circle</span>
              <p className="text-gray-500 font-medium">Okta er avslutta</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
